import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings, Moon, Sun, Monitor, Save, User, Bell, Lock, Database } from 'lucide-react';
import { AdminLayout } from './components/AdminLayout';
import { AdminTopBar } from './components/AdminTopBar';
import { useTheme } from './components/ThemeContext';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

function AdminSettingsContent() {
    const navigate = useNavigate();
    const { theme, setTheme } = useTheme();

    useEffect(() => {
        const adminAuth = localStorage.getItem('adminAuth');
        if (!adminAuth) {
            navigate('/admin');
        }
    }, [navigate]);

    const handleThemeChange = (newTheme: 'dark' | 'light' | 'system') => {
        setTheme(newTheme);
        toast.success(`Theme changed to ${newTheme} mode`);
    };

    const themeOptions = [
        {
            value: 'dark' as const,
            label: 'Dark',
            icon: Moon,
            description: 'Dark theme for reduced eye strain',
            gradient: 'from-slate-900 to-slate-800'
        },
        {
            value: 'light' as const,
            label: 'Light',
            icon: Sun,
            description: 'Light theme for bright environments',
            gradient: 'from-slate-50 to-slate-100'
        },
        {
            value: 'system' as const,
            label: 'System',
            icon: Monitor,
            description: 'Automatically match system preference',
            gradient: 'from-blue-900 to-purple-900'
        }
    ];

    const settingsSections = [
        {
            title: 'Account Settings',
            icon: User,
            description: 'Manage your account details and preferences',
            items: [
                { label: 'Email', value: 'admin@amasqis.ai' },
                { label: 'Role', value: 'Super Admin' },
                { label: 'Last Login', value: 'Today at 10:30 AM' }
            ]
        },
        {
            title: 'Notifications',
            icon: Bell,
            description: 'Configure notification preferences',
            items: [
                { label: 'Email Notifications', value: 'Enabled' },
                { label: 'Push Notifications', value: 'Enabled' },
                { label: 'Weekly Reports', value: 'Enabled' }
            ]
        },
        {
            title: 'Security',
            icon: Lock,
            description: 'Manage security settings',
            items: [
                { label: 'Two-Factor Auth', value: 'Disabled' },
                { label: 'Session Timeout', value: '30 minutes' },
                { label: 'Password Last Changed', value: '30 days ago' }
            ]
        },
        {
            title: 'Database',
            icon: Database,
            description: 'Database and storage settings',
            items: [
                { label: 'Storage Used', value: '2.4 GB / 10 GB' },
                { label: 'Auto Backup', value: 'Enabled (Daily)' },
                { label: 'Last Backup', value: 'Today at 3:00 AM' }
            ]
        }
    ];

    return (
        <>
            <AdminTopBar
                title="Settings"
                description="Manage your preferences and system configuration"
            />

            <div className="p-8 space-y-8">
                {/* Theme Selection */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-strong rounded-xl border border-primary/30 p-6"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                            <Settings className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-foreground text-xl">Appearance</h2>
                            <p className="text-sm text-muted-foreground">Customize how the admin dashboard looks</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <h3 className="text-foreground mb-4">Theme Preference</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {themeOptions.map((option) => {
                                    const Icon = option.icon;
                                    const isActive = theme === option.value;

                                    return (
                                        <motion.button
                                            key={option.value}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => handleThemeChange(option.value)}
                                            className={`
                                                relative p-6 rounded-xl border-2 transition-all duration-300
                                                ${isActive
                                                    ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20'
                                                    : 'border-primary/20 glass hover:border-primary/40'
                                                }
                                            `}
                                        >
                                            {/* Active Indicator */}
                                            {isActive && (
                                                <motion.div
                                                    layoutId="activeTheme"
                                                    className="absolute top-3 right-3 w-3 h-3 rounded-full bg-primary"
                                                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                                />
                                            )}

                                            {/* Icon with background */}
                                            <div className={`
                                                mx-auto w-16 h-16 rounded-xl
                                                flex items-center justify-center mb-4 transition-all duration-300
                                                ${isActive 
                                                    ? 'bg-white ring-2 ring-primary ring-offset-2 ring-offset-background' 
                                                    : 'bg-black'
                                                }
                                            `}>
                                                <Icon className={`w-8 h-8 transition-colors duration-300 ${isActive ? 'text-black' : 'text-white'}`} />
                                            </div>

                                            {/* Label */}
                                            <h4 className={`
                                                mb-2 transition-colors
                                                ${isActive ? 'text-primary' : 'text-foreground'}
                                            `}>
                                                {option.label}
                                            </h4>

                                            {/* Description */}
                                            <p className="text-sm text-muted-foreground text-center">
                                                {option.description}
                                            </p>

                                            {/* Active badge */}
                                            {isActive && (
                                                <div className="mt-3 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs inline-block">
                                                    Active
                                                </div>
                                            )}
                                        </motion.button>
                                    );
                                })}
                            </div>
                        </div>

                        {theme === 'system' && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30"
                            >
                                <div className="flex items-start gap-3">
                                    <Monitor className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-sm text-foreground">
                                            System theme is currently active
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            The dashboard will automatically switch between light and dark mode based on your device's system preferences.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Other Settings Sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {settingsSections.map((section, index) => {
                        const Icon = section.icon;
                        return (
                            <motion.div
                                key={section.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="glass-strong rounded-xl border border-primary/30 p-6"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                                        <Icon className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-foreground">{section.title}</h3>
                                        <p className="text-xs text-muted-foreground">{section.description}</p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    {section.items.map((item, itemIndex) => (
                                        <div
                                            key={itemIndex}
                                            className="flex items-center justify-between p-3 rounded-lg glass hover:border-primary/30 transition-all duration-300"
                                        >
                                            <span className="text-sm text-muted-foreground">{item.label}</span>
                                            <span className="text-sm text-foreground font-medium">{item.value}</span>
                                        </div>
                                    ))}
                                </div>

                                <Button
                                    variant="outline"
                                    className="w-full mt-4 border-primary/30 hover:bg-primary/10 hover:border-primary/50 text-foreground"
                                >
                                    Configure
                                </Button>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Save Changes Button */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="flex justify-end"
                >
                    <Button
                        onClick={() => toast.success('Settings saved successfully!')}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-8"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Save All Changes
                    </Button>
                </motion.div>
            </div>
        </>
    );
}

export function AdminSettings() {
    return (
        <AdminLayout>
            <AdminSettingsContent />
        </AdminLayout>
    );
}
